import { ConfigProvider, type ConfigProviderProps, theme } from 'antd';
import type { ReactNode } from 'react';

type Size = ConfigProviderProps['componentSize'];

export function FormControlSizing({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();

  function controlHeight(size: Size) {
    if (size === 'small') return token.controlHeightSM;
    if (size === 'large') return token.controlHeightLG;
    return token.controlHeight;
  }

  // Safari can give auto-height native inputs a 23px font box despite a 22px
  // line-height. Use AntD's semantic styles and size tokens instead of changing
  // typography or relying on the native input's intrinsic height.
  const input: ConfigProviderProps['input'] = {
    styles: ({ props }) => ({
      root: { height: controlHeight(props.size) },
      input: { height: '100%' },
    }),
  };
  const inputNumber: ConfigProviderProps['inputNumber'] = {
    styles: ({ props }) => ({
      root: { height: controlHeight(props.size) },
      input: {
        height:
          controlHeight(props.size) -
          (props.variant === 'borderless' ? 0 : 2 * token.lineWidth),
      },
    }),
  };
  function pickerStyles({ props }: { props: { size?: Size; multiple?: boolean } }) {
    if (props.multiple) return {};
    // TimePicker resolves inherited size after its outer semantic callback.
    // One line-height follows the picker's actual AntD typography tokens at
    // every size, including nested ConfigProvider and per-control overrides.
    return { input: { height: '1lh' } };
  }

  return (
    <ConfigProvider
      input={input}
      inputNumber={inputNumber}
      datePicker={{ styles: pickerStyles }}
      timePicker={{ styles: pickerStyles }}
    >
      {children}
    </ConfigProvider>
  );
}
